<template>
  <div class="precision-form-item__account-name">
    <UIInputSelect
      v-if="isJustCreated"
      v-model="path"
      :data="listValues"
      size="sm"
    />
    <template v-else>{{ accountName }}</template>
  </div>
  <div class="precision-form-item__amount">
    <UIInputPercent v-model="amount" size="sm" @ready="handleReady" />
    <UIButton
      v-if="isJustCreated"
      v-tooltip.top="'Delete owner'"
      variant="danger"
      fill="light"
      size="sm"
      :leading="TrashIcon"
      @click="handleClickDelete"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { DropdownListData } from '@types'

import { ASSET_FIELD } from '@/const'

import { useAssetsBunchStore } from '@/store/assets/bunch'

import { UIInputPercent, UIInputSelect } from '@ui/fields'
import { UIButton } from '@ui/buttons'
import { TrashIcon } from '@heroicons/vue/24/outline'

type Props = {
  index: number
  owner: [string[], number, boolean]
  listValues: DropdownListData[]
}

type Emits = {
  update: [index: number, value: [string[], number, boolean]]
  delete: [index: number]
}

const emit = defineEmits<Emits>()
const props = defineProps<Props>()

const assetsBunchStore = useAssetsBunchStore()

const path = computed({
  get: () => JSON.stringify(props.owner[0]),
  set: (value: string) => {
    emit('update', props.index, [JSON.parse(value), props.owner[1], props.owner[2]])
  },
})

const amount = computed({
  get: () => props.owner[1],
  set: (value: number) => {
    emit('update', props.index, [props.owner[0], value, props.owner[2]])
  },
})

const accountId = computed(() => props.owner[0].at(-2) || '')
const accountName = computed(() => assetsBunchStore.getElementById(accountId.value)?.field<string>(ASSET_FIELD.NAME).value)

const isJustCreated = computed(() => props.owner[2])

const handleReady = (el: HTMLInputElement) => {
  if (props.index !== 0) return
  el.focus()
}

const handleClickDelete = () => {
  emit('delete', props.index)
}
</script>

<style scoped>
.precision-form-item {
  &__account-name {
    @apply flex items-center;
    @apply text-sm font-medium;
    @apply truncate;

    .v-popper {
      @apply w-full;
    }
  }

  &__amount {
    @apply flex items-center gap-x-0.5;
  }
}
</style>
